import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LogOut, Plus, List, Users, Settings, Copy, Check, User, CreditCard, AlertTriangle, Gift, UserCircle, Upload, History, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import RafflesList from '../components/admin/RafflesList';
import CreateRaffle from '../components/admin/CreateRaffle';
import EditRaffle from '../components/admin/EditRaffle';
import RaffleManagement from '../components/admin/RaffleManagement';
import PendingAdmins from '../components/admin/PendingAdmins';
import AdminSettings from '../components/admin/AdminSettings';
import SubscriptionManager from '../components/admin/SubscriptionManager';
import SorteosList from '../components/admin/SorteosList';
import CreateSorteo from '../components/admin/CreateSorteo';
import EditSorteo from '../components/admin/EditSorteo';
import SorteoManagement from '../components/admin/SorteoManagement';
import ClientesList from '../components/admin/ClientesList';
import CreateSorteoClientes from '../components/admin/CreateSorteoClientes';
import CreateRifaClientes from '../components/admin/CreateRifaClientes';
import ImportarClientes from '../components/admin/ImportarClientes';
import ActivityHistory from '../components/admin/ActivityHistory';
import TrashSection from '../components/admin/TrashSection';

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setAdminData(response.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Error al cargar datos del administrador');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  const handleCopyLink = async () => {
    if (!adminData?.slug) {
      toast.error('Error: slug no disponible');
      console.error('Admin data:', adminData);
      return;
    }
    
    const publicLink = `${window.location.origin}/rifas/${adminData.slug}`;
    
    try {
      // Intentar con clipboard API primero
      await navigator.clipboard.writeText(publicLink);
      setCopiedLink(true);
      toast.success('Link copiado al portapapeles');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error('Clipboard error:', error);
      
      // Fallback: crear un input temporal y copiarlo
      try {
        const textArea = document.createElement('textarea');
        textArea.value = publicLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        setCopiedLink(true);
        toast.success('Link copiado al portapapeles');
        setTimeout(() => setCopiedLink(false), 2000);
      } catch (fallbackError) {
        // Si todo falla, mostrar mensaje con el link
        toast.info(`Tu link: ${publicLink}`, { duration: 10000 });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="admin-dashboard-title">Panel de Administración</h1>
            <p className="text-purple-200 text-sm">Bienvenido, {adminData?.name}</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="bg-white/10 text-white hover:bg-white/20 border-white/30"
            data-testid="logout-button"
          >
            <LogOut className="mr-2" size={20} />
            Cerrar Sesión
          </Button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-md overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-2 md:space-x-4 py-3 min-w-max">
            <Link to="/admin">
              <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-raffles">
                <List className="mr-1 md:mr-2" size={18} />
                Rifas
              </Button>
            </Link>
            <Link to="/admin/create">
              <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-create">
                <Plus className="mr-1 md:mr-2" size={18} />
                Crear
              </Button>
            </Link>
            <Link to="/admin/sorteos">
              <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-sorteos">
                <Gift className="mr-1 md:mr-2" size={18} />
                Sorteos
              </Button>
            </Link>
            <Link to="/admin/clientes">
              <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-clientes">
                <UserCircle className="mr-1 md:mr-2" size={18} />
                Clientes
              </Button>
            </Link>
            <Link to="/admin/settings">
              <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-settings">
                <User className="mr-1 md:mr-2" size={18} />
                Mi Perfil
              </Button>
            </Link>
            <Link to="/admin/history">
              <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-history">
                <History className="mr-1 md:mr-2" size={18} />
                Historial
              </Button>
            </Link>
            <Link to="/admin/trash">
              <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-trash">
                <Trash2 className="mr-1 md:mr-2" size={18} />
                Papelera
              </Button>
            </Link>
            {adminData?.is_owner && (
              <>
                <Link to="/admin/pending">
                  <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-pending">
                    <Users className="mr-1 md:mr-2" size={18} />
                    Pendientes
                  </Button>
                </Link>
                <Link to="/admin/subscriptions">
                  <Button variant="ghost" className="flex items-center text-sm md:text-base" data-testid="nav-subscriptions">
                    <CreditCard className="mr-1 md:mr-2" size={18} />
                    Suscripciones
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Links Personalizados */}
      <div className="max-w-7xl mx-auto px-4 pt-6 space-y-4">
        {/* Banner de estado de suscripción para admins no-owner */}
        {!adminData?.is_owner && adminData?.subscription_status && (
          <SubscriptionBanner adminData={adminData} />
        )}
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Routes>
          <Route path="/" element={<RafflesList />} />
          <Route path="/create" element={<CreateRaffle adminData={adminData} />} />
          <Route path="/edit/:raffleId" element={<EditRaffle />} />
          <Route path="/manage/:raffleId" element={<RaffleManagement />} />
          <Route path="/sorteos" element={<SorteosList />} />
          <Route path="/sorteos/create" element={<CreateSorteo adminData={adminData} />} />
          <Route path="/sorteos/create-clientes" element={<CreateSorteoClientes />} />
          <Route path="/sorteos/edit/:sorteoId" element={<EditSorteo />} />
          <Route path="/sorteos/manage/:sorteoId" element={<SorteoManagement />} />
          <Route path="/clientes" element={<ClientesList />} />
          <Route path="/clientes/importar" element={<ImportarClientes />} />
          <Route path="/rifas/create-clientes" element={<CreateRifaClientes />} />
          <Route path="/settings" element={<AdminSettings />} />
          <Route path="/history" element={<ActivityHistory />} />
          <Route path="/trash" element={<TrashSection />} />
          {adminData?.is_owner && (
            <>
              <Route path="/pending" element={<PendingAdmins />} />
              <Route path="/subscriptions" element={<SubscriptionManager />} />
            </>
          )}
        </Routes>
      </main>
    </div>
  );
};

// Componente para mostrar estado de suscripción al admin
const SubscriptionBanner = ({ adminData }) => {
  const getDaysRemaining = () => {
    if (!adminData.subscription_end_date) return 0;
    const end = new Date(adminData.subscription_end_date);
    const now = new Date();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  };

  const daysRemaining = getDaysRemaining();
  const status = adminData.subscription_status;

  if (status === 'expired' || !adminData.enabled) {
    return (
      <Card className="bg-red-50 border-red-300">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-red-600" size={24} />
            <div className="flex-1">
              <p className="font-semibold text-red-800">
                {!adminData.enabled ? 'Tu cuenta está deshabilitada' : 'Tu suscripción ha vencido'}
              </p>
              <p className="text-sm text-red-600">
                No podés crear nuevas rifas. Contactá al administrador para renovar.
              </p>
            </div>
            <Badge variant="destructive">Vencido</Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === 'trial') {
    return (
      <Card className={`${daysRemaining <= 1 ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-300'}`}>
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className={`font-semibold ${daysRemaining <= 1 ? 'text-amber-800' : 'text-blue-800'}`}>
                Período de prueba
              </p>
              <p className={`text-sm ${daysRemaining <= 1 ? 'text-amber-600' : 'text-blue-600'}`}>
                Te quedan {daysRemaining} día{daysRemaining !== 1 ? 's' : ''} de prueba gratis
              </p>
            </div>
            <Badge className={daysRemaining <= 1 ? 'bg-amber-500' : 'bg-blue-500'}>
              {daysRemaining} día{daysRemaining !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === 'active' && daysRemaining <= 5) {
    return (
      <Card className="bg-amber-50 border-amber-300">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-amber-600" size={24} />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">Tu suscripción vence pronto</p>
              <p className="text-sm text-amber-600">
                Te quedan {daysRemaining} día{daysRemaining !== 1 ? 's' : ''}. Renová para seguir creando rifas.
              </p>
            </div>
            <Badge className="bg-amber-500">{daysRemaining} días</Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Suscripción activa con tiempo suficiente
  return null;
};

export default AdminDashboard;
